import { Transition } from "@headlessui/react";
import { ChevronRightIcon } from "@xola/icons";
import { Alert, FadeIn } from "@xola/ui-kit";
import clsx from "clsx";
import { range } from "lodash";
import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import { Permission, Traveler, UserType } from "types";
import { useTraveler } from "../../../hooks/resources/useTraveler";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInfiniteCollection } from "../../../hooks/useInfiniteCollection";
import { NoResults } from "../../Common/NoResults";
import { ErrorBoundary } from "../../ErrorBoundary";
import { Loader } from "../../Loader";
import { ContactDetails } from "./ContactDetails";
import { ContactItem, ContactItemSkeleton } from "./ContactItem";

interface ContactListProps {
    height: number;
    search: string;
}

export const ContactList = ({ height, search }: ContactListProps) => {
    const history = useHistory();
    const urlParams = useParams<{ customerId?: ID }>();
    const { isGranted } = useAuthorization();

    const fetchParams: any = { type: UserType.TRAVELER };
    if (search.length > 0) {
        fetchParams.search = search;
    }

    const {
        data: contacts,
        isLoading,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
    } = useInfiniteCollection<Traveler>("users", fetchParams, {
        keepPreviousData: true,
        staleTime: 2000, // Only re-fetch if the change happens within 2s
    });

    const isCustomerInContacts = urlParams.customerId
        ? contacts.some((contact) => contact.id === urlParams.customerId)
        : false;

    // If the requested traveler was not present in the previous response, fetch them separately
    const { data: singleContact, isLoadingTraveler } = useTraveler(urlParams.customerId, null, {
        enabled: isCustomerInContacts && isGranted(Permission.MODIFY_CUSTOMER_DETAILS),
    });

    const contactsList = isCustomerInContacts ? contacts : [...contacts, singleContact];

    const selectedContact = contactsList.find((contact) => {
        return contact && contact.id === urlParams.customerId;
    });

    const isCollapsed = !!selectedContact;
    const handleTemplateItemClick = (contact: Traveler) => history.push(`/customers/contacts/${contact.id}`);
    const handleExpandIconClick = () => history.push(`/customers/contacts`);

    if (isLoading || isLoadingTraveler) {
        return (
            <>
                {range(1, 10).map((index) => (
                    <ContactItemSkeleton key={index} />
                ))}
            </>
        );
    }

    // Some items in contactsList array is undefined
    if (contactsList.every((contact) => !contact)) {
        return <NoResults>No results found</NoResults>;
    }

    return (
        <div className="flex h-full lg:flex-row">
            <FadeIn
                tag="span"
                // We need ad-hoc classes otherwise Transition will not add them back after animation complete because
                // they are same as `enterFrom` and `enterTo` classes
                className={clsx("flex", isCollapsed ? "w-56 flex-shrink-0 flex-col lg:w-56 xl:w-60" : "w-[100%]")}
                enter="transition-all duration-250"
                enterFrom={clsx(isCollapsed ? "w-full" : "opacity-0")}
                enterTo={clsx(isCollapsed ? "w-56" : "opacity-100")}
            >
                {contactsList.length > 0 ? (
                    <>
                        <Virtuoso
                            style={{ height }}
                            className={clsx("w-full", isCollapsed && "border-r border-gray-light scrollbar-hide")}
                            data={contactsList}
                            endReached={async () => {
                                if (hasNextPage) {
                                    fetchNextPage();
                                }
                            }}
                            itemContent={(index, contact) => {
                                return (
                                    contact && (
                                        <ErrorBoundary fallbackMessage={`Error rendering contact ${contact.name}`}>
                                            <ContactItem
                                                key={contact.id}
                                                contact={contact}
                                                isActive={contact.id === selectedContact?.id}
                                                isExpanded={!isCollapsed}
                                                onClick={handleTemplateItemClick}
                                            />
                                        </ErrorBoundary>
                                    )
                                );
                            }}
                            components={{ Footer: () => (isFetchingNextPage ? <Loader /> : null) }}
                        />
                        {isCollapsed && (
                            <div
                                data-testid="contact-list-collapse-button"
                                className="absolute mt-5 ml-56 cursor-pointer rounded-full border border-gray-light bg-white px-2 py-1"
                                onClick={handleExpandIconClick}
                            >
                                <ChevronRightIcon size="small" />
                            </div>
                        )}
                    </>
                ) : (
                    <NoContactsMessage search={search} isLoading={isLoading} />
                )}
            </FadeIn>

            {isCollapsed && (
                <Transition
                    show={isCollapsed}
                    appear={isCollapsed}
                    as="div"
                    className="flex w-full flex-col overflow-y-auto px-5 py-3 pr-3 xl:px-10 xl:py-5"
                    enter="transition-all duration-250"
                    enterFrom="transform translate-x-full"
                    enterTo="transform translate-x-0"
                >
                    {selectedContact ? (
                        <ErrorBoundary>
                            <ContactDetails contact={selectedContact} />
                        </ErrorBoundary>
                    ) : null}
                </Transition>
            )}
        </div>
    );
};

const NoContactsMessage = ({ search, isLoading }: { search: string; isLoading: boolean }) => {
    return (
        <div className="m-4 mx-2 w-full p-2 text-gray-darker">
            {isLoading ? (
                <Loader size="tiny" className="!p-1" text="Loading Contacts" />
            ) : (
                <Alert color="secondary" className="w-full">
                    {search ? (
                        <>
                            No contacts found that match &quot;<var data-var="serarch">{search}</var>&quot;
                        </>
                    ) : (
                        "No contacts found"
                    )}
                </Alert>
            )}
        </div>
    );
};
